/**
 * Enterprise-grade barcode formatter.
 * - Preserves leading zeros.
 * - Default "smart" strategy groups into 3–4 digit chunks to avoid lonely 1–2 digit tails.
 * - Supports fixed-size and explicit pattern strategies.
 * - Safe by default: normalizes/validates input and throws actionable errors.
 *
 * Example:
 *   formatBarcode("005479113609").formatted  // "0054 7911 3609"
 */

export type BarcodeFormatStrategy = "smart" | "fixed" | "pattern";

export interface FormatBarcodeOptions {
  /** Grouping strategy. Default: "smart". */
  strategy?: BarcodeFormatStrategy;

  /** Separator between groups. Default: single space. */
  separator?: string;

  /** When strategy === "fixed": the fixed chunk size (2–8 recommended). */
  fixedSize?: number;

  /** When strategy === "pattern": explicit group sizes, e.g., [3, 4, 3, 3]. */
  pattern?: number[];

  /**
   * Normalize input by removing spaces, tabs, hyphens, and underscores.
   * Default: true
   */
  normalize?: boolean;

  /**
   * Allow alphanumeric (A–Z, 0–9) content.
   * Default: false (digits only). If true, case is preserved.
   */
  allowAlphanumeric?: boolean;

  /** Preferred group size for "smart" strategy. Default: 4. */
  preferredGroup?: number;

  /** Alternate group size for "smart" strategy. Default: 3. */
  altGroup?: number;

  /** Minimum group size allowed for "smart" strategy endings. Default: 2. */
  minTailGroup?: number;

  /** If provided, hard limit on maximum length allowed (defensive). */
  maxLength?: number;

  /** If true, trim() the incoming string. Default: true. */
  trim?: boolean;
}

export interface FormattedBarcode {
  /** Original input (after optional trim/normalize). */
  original: string;
  /** Array of grouped substrings (preserves character order). */
  groups: string[];
  /** Groups joined by the chosen separator. */
  formatted: string;
  /** True if content contains only digits (after normalization). */
  isNumeric: boolean;
  /** Length of the normalized content. */
  length: number;
}

/**
 * Format a barcode string into human-readable groups.
 */
export function formatBarcode(input: string, opts: FormatBarcodeOptions = {}): FormattedBarcode {
  const {
    strategy = "smart",
    separator = " ",
    fixedSize,
    pattern,
    normalize = true,
    allowAlphanumeric = false,
    preferredGroup = 4,
    altGroup = 3,
    minTailGroup = 2,
    maxLength,
    trim = true,
  } = opts;

  if (typeof input !== "string") {
    throw new TypeError("formatBarcode: input must be a string.");
  }

  // Step 1: sanitize/normalize
  let s = trim ? input.trim() : input;

  if (normalize) {
    // Remove common visual separators while preserving leading zeros and content
    s = s.replace(/[ _\t-]+/g, "");
  }

  if (s.length === 0) {
    throw new Error("formatBarcode: input is empty after normalization.");
  }

  if (maxLength != null && s.length > maxLength) {
    throw new Error(`formatBarcode: input exceeds maximum length of ${maxLength}.`);
  }

  // Validate allowed charset
  const isNumeric = /^[0-9]+$/.test(s);
  if (!allowAlphanumeric && !isNumeric) {
    throw new Error("formatBarcode: non-digit characters found and allowAlphanumeric=false.");
  }
  if (allowAlphanumeric && !/^[A-Za-z0-9]+$/.test(s)) {
    throw new Error(
      "formatBarcode: invalid characters; only A–Z and 0–9 allowed when allowAlphanumeric=true."
    );
  }

  // Step 2: choose grouping
  let groups: string[];
  switch (strategy) {
    case "fixed":
      if (!fixedSize || fixedSize < 2) {
        throw new Error("formatBarcode: fixed strategy requires fixedSize >= 2.");
      }
      groups = chunkFixed(s, fixedSize);
      break;

    case "pattern":
      if (!pattern || pattern.length === 0 || !pattern.every((n) => Number.isInteger(n) && n > 0)) {
        throw new Error(
          "formatBarcode: pattern strategy requires a non-empty pattern of positive integers."
        );
      }
      groups = chunkPattern(s, pattern);
      break;

    case "smart":
    default:
      groups = chunkSmart(s, {
        preferred: preferredGroup,
        alt: altGroup,
        minTail: minTailGroup,
      });
      break;
  }

  return {
    original: s,
    groups,
    formatted: groups.join(separator),
    isNumeric,
    length: s.length,
  };
}

/** Fixed-size chunking: splits into equal pieces; last chunk may be shorter. */
function chunkFixed(s: string, size: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length; i += size) {
    out.push(s.slice(i, i + size));
  }
  return out;
}

/**
 * Pattern chunking: consumes sizes from the pattern left-to-right.
 * If remaining characters exceed the sum of pattern sizes, the last size repeats.
 * If the pattern sum exceeds the string length, the final chunk will be shorter.
 */
function chunkPattern(s: string, pattern: number[]): string[] {
  const out: string[] = [];
  let i = 0;
  let pIdx = 0;

  while (i < s.length) {
    const size = pattern[Math.min(pIdx, pattern.length - 1)];
    out.push(s.slice(i, i + size));
    i += size;
    pIdx++;
  }
  return out;
}

/**
 * Smart chunking:
 * - Targets readable 3–4 sized chunks.
 * - Avoids trailing lonely groups of size 1 (and, when feasible, 2).
 * - Keeps groups balanced and predictable.
 *
 * Strategy:
 *  1) Prefer groups of `preferred` (default 4).
 *  2) If remainder would be 1 → take one leading `alt` (default 3), then preferred.
 *  3) If remainder would be 2 and length is large → take two leading `alt` groups, then preferred.
 *  4) Final tail groups are never size 1; size 2 is allowed if unavoidable on very short inputs.
 */
function chunkSmart(s: string, cfg: { preferred: number; alt: number; minTail: number }): string[] {
  const { preferred, alt, minTail } = cfg;
  if (preferred <= alt) {
    throw new Error("chunkSmart invariant: preferred group must be greater than alt group.");
  }
  const n = s.length;
  const out: string[] = [];

  if (n <= preferred) return [s];

  const r = n % preferred;

  let i = 0;
  const push = (k: number) => {
    out.push(s.slice(i, i + k));
    i += k;
  };

  if (r === 1 && n > preferred + alt) {
    push(alt);
    while (i + preferred <= n) push(preferred);
    if (i < n) push(n - i);
    balanceTail(out, minTail);
    return out;
  }

  if (r === 2 && n >= preferred * 2 + alt * 2) {
    push(alt);
    push(alt);
    while (i + preferred <= n) push(preferred);
    if (i < n) push(n - i);
    balanceTail(out, minTail);
    return out;
  }

  while (i + preferred + minTail <= n) push(preferred);
  if (i < n) push(n - i);
  balanceTail(out, minTail);
  return out;
}

/**
 * Ensures last group isn't smaller than minTail by borrowing a digit
 * from the previous group when possible (e.g., [4,1] -> [3,2]).
 */
function balanceTail(groups: string[], minTail: number): void {
  if (groups.length < 2) return;
  const last = groups[groups.length - 1];
  if (last.length >= minTail) return;

  const prev = groups[groups.length - 2];
  if (prev.length <= 1) return; // nothing to borrow

  // Borrow one character from the previous group
  const borrowed = prev.slice(-1);
  groups[groups.length - 2] = prev.slice(0, -1);
  groups[groups.length - 1] = borrowed + last;
}

/* ---------------------------
 * Convenience helpers
 * --------------------------*/

/**
 * Quick helper for your common case:
 * - Digits only
 * - Smart 3–4 grouping
 * - Space separator
 */
export function formatBarcodeDefault(input: string): string {
  return formatBarcode(input, {
    strategy: "smart",
    separator: " ",
    normalize: true,
    allowAlphanumeric: false,
    preferredGroup: 4,
    altGroup: 3,
    minTailGroup: 2,
  }).formatted;
}
